import { Component, ErrorInfo, ReactNode } from 'react';

// third-party
import { FormattedMessage } from 'react-intl';

// material-ui
import { Button, Stack, Typography } from '@mui/material';

// ==============================|| ERROR BOUNDARY ||============================== //
//
// Wraps the routed page, not the whole app: a render error in one table leaves the navigation and
// the header usable, so the admin can walk to another page instead of facing a blank screen.

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

const ErrorFallback = () => (
  <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ minHeight: '50vh', textAlign: 'center' }}>
    <Typography variant="h4">
      <FormattedMessage id="error.render.title" />
    </Typography>
    <Typography variant="body2" color="textSecondary">
      <FormattedMessage id="error.render.text" />
    </Typography>
    <Button variant="contained" onClick={() => window.location.reload()}>
      <FormattedMessage id="error.render.reload" />
    </Button>
  </Stack>
);

class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Console render error', error, info.componentStack);
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return <ErrorFallback />;
  }
}

export default ErrorBoundary;
